import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { submissionsAPI } from '../../services/api'
import {
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  LinkIcon,
  StarIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { format } from 'date-fns'
import { toast } from 'react-hot-toast'

const statusIcons = {
  submitted: ClockIcon,
  under_review: ExclamationTriangleIcon,
  evaluated: CheckCircleIcon,
}

const statusColors = {
  submitted: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  evaluated: 'bg-green-100 text-green-800',
}

export default function AdminSubmissionDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [showEvaluationForm, setShowEvaluationForm] = useState(false)
  const [evaluationData, setEvaluationData] = useState({ score: '', feedback: '' })

  // Fetch submission details
  const { data: submission, isLoading, error } = useQuery(
    ['admin-submission', id],
    () => submissionsAPI.getById(id),
    {
      select: (response) => response.data.submission
    }
  )

  // Evaluate submission mutation
  const evaluateMutation = useMutation(
    (evaluation) => submissionsAPI.evaluate(id, evaluation),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-submission', id])
        queryClient.invalidateQueries('admin-all-submissions')
        setShowEvaluationForm(false)
        setEvaluationData({ score: '', feedback: '' })
        toast.success('Submission evaluated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to evaluate submission')
      }
    }
  )

  const handleEvaluate = () => {
    setEvaluationData({
      score: submission.score || '',
      feedback: submission.feedback || ''
    })
    setShowEvaluationForm(true)
  }

  const handleSubmitEvaluation = (e) => {
    e.preventDefault()
    if (!evaluationData.score || evaluationData.score < 0 || evaluationData.score > 100) {
      toast.error('Please enter a valid score between 0 and 100')
      return
    }

    evaluateMutation.mutate({
      score: parseFloat(evaluationData.score),
      feedback: evaluationData.feedback
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !submission) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Submission not found</h3>
          <p className="text-gray-500 mb-4">The submission you're looking for doesn't exist or you don't have permission to view it.</p>
          <button
            onClick={() => navigate('/admin/submissions')}
            className="btn-primary"
          >
            Back to Submissions
          </button>
        </div>
      </div>
    )
  }

  const StatusIcon = statusIcons[submission.status]

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/admin/submissions')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                Submission Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Review and evaluate this submission
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Submission Overview */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {submission.title}
                  </h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      {submission.user_name || 'Unknown User'}
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {format(new Date(submission.created_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                    <div className="flex items-center">
                      <StatusIcon className="h-4 w-4 mr-1" />
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[submission.status]}`}>
                        {submission.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {submission.description}
                  </p>
                </div>
                
                {submission.status !== 'evaluated' && (
                  <div className="ml-6">
                    <button
                      onClick={handleEvaluate}
                      className="btn-primary flex items-center"
                    >
                      <StarIcon className="h-4 w-4 mr-2" />
                      Evaluate
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Challenge Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Challenge Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Challenge</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Link
                      to={`/challenges/${submission.challenge_id}`}
                      className="text-purple-600 hover:text-purple-500"
                    >
                      {submission.challenge_title || 'Unknown Challenge'}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Submission ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">#{submission.id}</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Links and Resources */}
          {(submission.repository_url || submission.demo_url) && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Links & Resources</h3>
                <div className="space-y-3">
                  {submission.repository_url && (
                    <div className="flex items-center">
                      <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Repository</dt>
                        <dd className="text-sm">
                          <a
                            href={submission.repository_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-500"
                          >
                            {submission.repository_url}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}
                  {submission.demo_url && (
                    <div className="flex items-center">
                      <LinkIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Demo</dt>
                        <dd className="text-sm">
                          <a
                            href={submission.demo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-500"
                          >
                            {submission.demo_url}
                          </a>
                        </dd>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Files */}
          {submission.files && submission.files.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Uploaded Files</h3>
                <div className="space-y-3">
                  {submission.files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <DocumentArrowDownIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {file.originalName || file.filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(`/api/submissions/${submission.id}/files/${file.filename}`, '_blank')}
                        className="text-purple-600 hover:text-purple-500 text-sm font-medium"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Evaluation */}
          {submission.status === 'evaluated' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Score</dt>
                    <dd className="mt-1 flex items-center">
                      <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
                      <span className="text-lg font-semibold text-gray-900">
                        {submission.score}/100
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Evaluated On</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {submission.evaluated_at ? format(new Date(submission.evaluated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </dd>
                  </div>
                </div>
                {submission.feedback && (
                  <div className="mt-4">
                    <dt className="text-sm font-medium text-gray-500">Feedback</dt>
                    <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                      {submission.feedback}
                    </dd>
                  </div>
                )}
                <div className="mt-4">
                  <button
                    onClick={handleEvaluate}
                    className="text-purple-600 hover:text-purple-500 text-sm font-medium"
                  >
                    Update Evaluation
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Evaluation Modal */}
      {showEvaluationForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Evaluate Submission
                </h3>
                <button
                  onClick={() => setShowEvaluationForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium text-gray-900">{submission.title}</h4>
                <p className="text-sm text-gray-500">{submission.challenge_title}</p>
              </div>

              <form onSubmit={handleSubmitEvaluation}>
                <div className="mb-4">
                  <label htmlFor="score" className="block text-sm font-medium text-gray-700 mb-1">
                    Score (0-100) *
                  </label>
                  <input
                    type="number"
                    id="score"
                    min="0"
                    max="100"
                    step="0.1"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={evaluationData.score}
                    onChange={(e) => setEvaluationData(prev => ({ ...prev, score: e.target.value }))}
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback (Optional)
                  </label>
                  <textarea
                    id="feedback"
                    rows={4}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    value={evaluationData.feedback}
                    onChange={(e) => setEvaluationData(prev => ({ ...prev, feedback: e.target.value }))}
                    placeholder="Provide detailed feedback on the submission..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEvaluationForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={evaluateMutation.isLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    {evaluateMutation.isLoading ? 'Evaluating...' : 'Submit Evaluation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}